import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Play, Send } from "lucide-react";
import api, { apiError } from "../lib/api";
import { fmtDate, PRIORITY, TASK_STATUS } from "../lib/labels";
import { userName, useUsers } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { Chip, ReasonsList } from "./common";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Textarea } from "./ui/textarea";

export default function TaskDialog({ taskId, open, onClose, onChanged }) {
  const { user } = useAuth();
  const { map } = useUsers();
  const qc = useQueryClient();
  const [comment, setComment] = useState("");

  const { data, refetch } = useQuery({
    queryKey: ["task", taskId],
    queryFn: async () => (await api.get(`/tasks/${taskId}`)).data,
    enabled: !!taskId && open,
  });

  const task = data?.task;
  const changed = () => { refetch(); onChanged?.(); qc.invalidateQueries({ queryKey: ["dashboard-badges"] }); };

  const act = async (action, extra = {}) => {
    try {
      await api.post(`/tasks/${taskId}/actions`, { action, ...extra });
      toast.success("Acción ejecutada");
      changed();
    } catch (e) {
      const reasons = e?.response?.data?.detail?.reasons;
      if (reasons) toast.error("Acción bloqueada", { description: reasons.map((r) => r.message).join(" · ") });
      else toast.error(apiError(e));
      refetch();
    }
  };

  const canReview = task?.status === "en_revision" && (task?.reviewer_id === user?.id || user?.roles?.includes("REVIEWER") || user?.roles?.includes("DEMO_ADMIN")) && task?.assignee_id !== user?.id;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="task-dialog">
        {!task ? (
          <>
            <DialogHeader><DialogTitle>Cargando tarea…</DialogTitle></DialogHeader>
            <p className="py-8 text-center text-sm text-slate-500">Cargando tarea…</p>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="pr-6">{task.title}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-wrap items-center gap-2">
              <Chip map={TASK_STATUS} value={task.status} testid="task-status-chip" />
              <Chip map={PRIORITY} value={task.priority} />
              {task.requires_evidence && <span className="ts-chip ts-chip-warning">Requiere evidencia</span>}
              {task.requires_review && <span className="ts-chip ts-chip-ai">Requiere revisión {task.review_decision ? `(${task.review_decision})` : ""}</span>}
              {data.expediente && (
                <Link to={`/expedientes/${data.expediente.id}`} onClick={onClose} className="text-xs text-blue-700 hover:underline" data-testid="task-expediente-link">
                  {data.expediente.code} — {data.expediente.title}
                </Link>
              )}
              {task.project_id && (
                <Link to={`/proyectos/${task.project_id}`} onClick={onClose} className="ts-chip ts-chip-info" data-testid="task-project-link">
                  Abrir proyecto →
                </Link>
              )}
            </div>
            {task.description && <p className="mt-2 text-sm text-slate-600">{task.description}</p>}
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-500 sm:grid-cols-4">
              <span>Responsable: <strong className="text-slate-700">{userName(map, task.assignee_id)}</strong></span>
              <span>Creador: <strong className="text-slate-700">{userName(map, task.creator_id)}</strong></span>
              <span>Vence: <strong className="text-slate-700">{fmtDate(task.due_date)}</strong></span>
              <span>Área: <strong className="text-slate-700">{task.area}</strong></span>
            </div>

            {data.evidence_requirement && (
              <div className="mt-3 rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs">
                Evidencia vinculada: <strong>{data.evidence_requirement.name}</strong> — estado{" "}
                <Chip map={{}} value={null} /> <Chip map={TASK_STATUS} value={task.status} testid="task-ev-req" />
                <span className="ml-1 font-medium">{data.evidence_requirement.status}</span>.{" "}
                <Link to={`/expedientes/${task.expediente_id}?tab=evidencias`} onClick={onClose} className="text-blue-700 underline">Gestionar evidencia →</Link>
              </div>
            )}

            {!data.can_complete && !["completada", "cancelada"].includes(task.status) && (
              <div className="mt-3">
                <p className="ts-caption mb-2">Controles pendientes para completar</p>
                <ReasonsList reasons={data.block_reasons} testid="task-block-reasons" />
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {task.status === "pendiente" && <Button size="sm" variant="outline" onClick={() => act("iniciar")} data-testid="task-start-button"><Play className="mr-1 h-3.5 w-3.5" /> Iniciar</Button>}
              {["pendiente", "en_curso", "esperando", "bloqueada", "en_revision"].includes(task.status) && (
                <Button size="sm" onClick={() => act("completar")} disabled={!data.can_complete} data-testid="task-complete-button">Completar</Button>
              )}
              {task.status === "en_curso" && task.requires_review && (
                <Button size="sm" variant="outline" onClick={() => act("enviar_revision")} data-testid="task-send-review-button"><Send className="mr-1 h-3.5 w-3.5" /> Enviar a revisión</Button>
              )}
              {canReview && (
                <>
                  <Button size="sm" onClick={() => act("review", { decision: "aprobar" })} data-testid="task-review-approve">Aprobar revisión</Button>
                  <Button size="sm" variant="outline" onClick={() => { const m = window.prompt("Motivo de la devolución:"); if (m) act("review", { decision: "devolver", motivo: m }); }} data-testid="task-review-return">Devolver</Button>
                </>
              )}
            </div>

            <div className="mt-4 border-t border-slate-100 pt-3">
              <p className="ts-caption mb-2">Comentarios</p>
              {(task.comments || []).map((c, i) => (
                <p key={i} className="mb-1 text-xs text-slate-600"><strong>{userName(map, c.by)}</strong>: {c.text}</p>
              ))}
              <div className="mt-2 flex gap-2">
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Añadir comentario…" className="min-h-[38px]" data-testid="task-comment-input" />
                <Button size="sm" variant="outline" disabled={!comment.trim()} onClick={() => { act("comentar", { text: comment }); setComment(""); }} data-testid="task-comment-button"><MessageSquare className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
